<template>
  <div class="page-container">
    <el-card class="booking-management-card">
      <template #header>
        <div class="card-header">
          <div class="header-left">
            <h2 class="page-title">{{ $t('booking.title') }}</h2>
          </div>
          <div class="header-right">
            <el-dropdown @command="handleExportCommand" trigger="click">
              <el-button type="success">
                <el-icon><Download /></el-icon>
                {{ $t('common.export') }}
                <el-icon class="el-icon--right"><ArrowDown /></el-icon>
              </el-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="xlsx">
                    <el-icon><Document /></el-icon>
                    Export as Excel (.xlsx)
                  </el-dropdown-item>
                  <el-dropdown-item command="csv">
                    <el-icon><Tickets /></el-icon>
                    Export as CSV (.csv)
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
            <el-button type="primary" class="add-btn" @click="goToAdd">
              <el-icon><Plus /></el-icon>
              {{ $t('booking.newBooking') }}
            </el-button>
          </div>
        </div>
      </template>

      <!-- Tab Navigation -->
      <el-tabs v-model="activeTab" class="booking-tabs">
        <!-- Tab 1: Weekly Schedule -->
        <el-tab-pane :label="$t('booking.weeklySchedule')" name="weekly">
          <!-- Week Calendar & View Controls -->
          <el-card class="week-calendar-card" shadow="never" v-if="dateRange && dateRange.length === 2">
            <div class="week-calendar">
              <div 
                v-for="(day, index) in weekDays" 
                :key="index"
                :class="['day-item', { 
                  'is-selected': isDaySelected(day),
                  'is-today': isToday(day)
                }]"
                @click="selectDay(day)"
              >
                <div class="day-name">{{ getDayName(day) }}</div>
                <div class="day-date">{{ getDayDate(day) }}</div>
              </div>
            </div>
            
            <el-divider />
            
            <div class="view-controls-inline">
              <span class="control-label">{{ $t('booking.groupBy') }}:</span>
              <el-radio-group v-model="groupByMode" size="small">
                <el-radio-button value="hour">{{ $t('booking.byHour') }}</el-radio-button>
                <el-radio-button value="teacher">{{ $t('booking.byTeacher') }}</el-radio-button>
                <el-radio-button value="customer">{{ $t('booking.byCustomer') }}</el-radio-button>
              </el-radio-group>
            </div>
          </el-card>

      <!-- Booking List -->
      <div class="booking-list" v-loading="loading">
        <div v-if="bookingStore.bookings.length === 0" class="empty-data">
          <el-empty :description="$t('booking.noBookingFound')" />
        </div>
        
        <div v-else class="booking-table-container">
          <!-- Hour-Based View -->
          <el-table 
            v-if="groupByMode === 'hour'" 
            :data="bookingsByHour" 
            stripe
            border
            class="booking-table hour-view-table"
          >
            <el-table-column prop="hour" :label="$t('booking.timeSlot')" width="120" fixed>
              <template #default="{ row }">
                <el-tag type="primary" size="large" effect="dark">{{ row.hour }}</el-tag>
              </template>
            </el-table-column>
            
            <el-table-column :label="$t('booking.hoursCount')" width="100" align="center">
              <template #default="{ row }">
                <el-tag type="primary" size="large" effect="plain">
                  {{ row.bookings.length }}
                </el-tag>
              </template>
            </el-table-column>
            
            <el-table-column :label="$t('booking.teachers')" min-width="200">
              <template #default="{ row }">
                <div class="tag-list">
                  <el-tag 
                    v-for="teacher in Array.from(row.teachers)" 
                    :key="teacher"
                    size="default"
                    type="success"
                    effect="light"
                    style="margin: 3px;"
                  >
                    <i class="iconfont icon-teacher" style="margin-right: 4px; font-size: 12px;"></i>
                    {{ teacher }}
                  </el-tag>
                </div>
              </template>
            </el-table-column>
            
            <el-table-column :label="$t('booking.customers')" min-width="200">
              <template #default="{ row }">
                <div class="tag-list">
                  <el-tag 
                    v-for="customer in Array.from(row.customers)" 
                    :key="customer"
                    size="default"
                    type="warning"
                    effect="light"
                    class="clickable-customer-tag"
                    @click="showCustomerInfo(row.customerMap.get(customer), customer)"
                  >
                    <i class="iconfont icon-helmet" style="margin-right: 4px; font-size: 12px;"></i>
                    <span class="customer-underline">{{ customer }}</span>
                  </el-tag>
                </div>
              </template>
            </el-table-column>
            
            <el-table-column :label="$t('booking.bookingDetails')" min-width="350">
              <template #default="{ row }">
                <div class="booking-details-list">
                  <div 
                    v-for="(booking, idx) in row.bookings" 
                    :key="idx"
                    class="booking-detail-item"
                  >
                    <el-tooltip placement="top">
                      <template #content>
                        <div>
                          <div><strong>{{ $t('booking.teacher') }}:</strong> {{ booking.name }}</div>
                          <div><strong>{{ $t('booking.customer') }}:</strong> {{ booking.customerName }}</div>
                          <div><strong>{{ $t('booking.date') }}:</strong> {{ formatBookingDate(booking) }}</div>
                          <div><strong>{{ $t('common.status') }}:</strong> {{ getStatusText(booking.status) }}</div>
                        </div>
                      </template>
                      <div class="booking-link-row">
                        <el-button link type="primary" size="default" @click="viewDetail(booking)" class="booking-link">
                          <i class="iconfont icon-teacher" style="margin-right: 4px;"></i>{{ booking.name }}
                        </el-button>
                        <el-icon style="margin: 0 6px; color: #909399;"><ArrowRight /></el-icon>
                        <el-button link type="warning" size="default" @click="viewDetail(booking)" class="booking-link">
                          <i class="iconfont icon-helmet" style="margin-right: 4px;"></i>{{ booking.customerName }}
                        </el-button>
                      </div>
                    </el-tooltip>
                  </div>
                </div>
              </template>
            </el-table-column>
          </el-table>
          
          <!-- Teacher-Based View -->
          <div v-else-if="groupByMode === 'teacher'" class="grouped-view">
            <div class="collapse-controls">
              <el-button 
                size="small" 
                @click="expandAllTeachers"
                :icon="Plus"
              >
                {{ $t('common.expandAll') }}
              </el-button>
              <el-button 
                size="small" 
                @click="collapseAllTeachers"
                :icon="Minus"
              >
                {{ $t('common.collapseAll') }}
              </el-button>
            </div>
            <el-collapse v-model="activeTeacherPanels" class="teacher-collapse">
              <el-collapse-item 
                v-for="(teacherData, index) in bookingsByTeacherWithHours" 
                :key="index"
                :name="index"
              >
                <template #title>
                  <div class="collapse-header">
                    <div class="header-main">
                      <el-icon class="expand-icon"><ArrowRight /></el-icon>
                      <h3>{{ teacherData.teacher }}</h3>
                    </div>
                    <div class="header-stats">
                      <el-tag type="success" effect="plain">{{ teacherData.totalBookings }} {{ getPluralText('booking', teacherData.totalBookings) }}</el-tag>
                      <el-tag type="info" effect="plain">{{ teacherData.customers.size }} {{ getPluralText('customer', teacherData.customers.size) }}</el-tag>
                    </div>
                  </div>
                </template>
                
                <el-table :data="teacherData.hoursArray" stripe border class="nested-table teacher-nested-table">
                  <el-table-column prop="hour" :label="$t('booking.timeSlot')" width="120">
                    <template #default="{ row }">
                      <el-tag type="primary" effect="dark">{{ row.hour }}</el-tag>
                    </template>
                  </el-table-column>
                  
                  <el-table-column :label="$t('booking.hoursCount')" width="100" align="center">
                    <template #default="{ row }">
                      <el-tag type="primary" effect="plain" size="large">
                        {{ row.bookings.length }}
                      </el-tag>
                    </template>
                  </el-table-column>
                  
                  <el-table-column :label="$t('booking.customers')" min-width="250">
                    <template #default="{ row }">
                      <div class="tag-list">
                        <el-tag 
                          v-for="(booking, idx) in row.bookings" 
                          :key="idx"
                          size="default"
                          type="warning"
                          effect="light"
                          style="margin: 3px; cursor: pointer;"
                          @click="showCustomerInfo(getCustomerIdFromBooking(booking), booking.customerName)"
                        >
                          <i class="iconfont icon-helmet" style="margin-right: 4px; font-size: 12px;"></i>
                          {{ booking.customerName }}
                        </el-tag>
                      </div>
                    </template>
                  </el-table-column>
                  
                  <el-table-column :label="$t('common.actions')" width="120" align="center">
                    <template #default="{ row }">
                      <el-button 
                        v-for="(booking, idx) in row.bookings" 
                        :key="idx"
                        type="primary" 
                        size="small"
                        @click="viewDetail(booking)"
                        :icon="Search"
                      >
                        {{ $t('common.detail') }}
                      </el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </el-collapse-item>
            </el-collapse>
          </div>
          
          <!-- Customer-Based View -->
          <div v-else-if="groupByMode === 'customer'" class="grouped-view">
            <div class="collapse-controls">
              <el-button 
                size="small" 
                @click="expandAllCustomers"
                :icon="Plus"
              >
                {{ $t('common.expandAll') }}
              </el-button>
              <el-button 
                size="small" 
                @click="collapseAllCustomers"
                :icon="Minus"
              >
                {{ $t('common.collapseAll') }}
              </el-button>
            </div>
            <el-collapse v-model="activeCustomerPanels" class="customer-collapse">
              <el-collapse-item 
                v-for="(customerData, index) in bookingsByCustomerWithHours" 
                :key="index"
                :name="index"
              >
                <template #title>
                  <div class="collapse-header">
                    <div class="header-main">
                      <el-icon class="expand-icon"><ArrowRight /></el-icon>
                      <h3>{{ customerData.customer }}</h3>
                      <!-- <el-icon :size="16" class="customer-info-icon" @click.stop="showCustomerInfo(getCustomerIdFromCustomerData(customerData), customerData.customer)">
                        <InfoFilled />
                      </el-icon> -->
                    </div>
                    <div class="header-stats">
                      <el-tag type="warning" effect="plain">{{ customerData.totalBookings }} {{ getPluralText('booking', customerData.totalBookings) }}</el-tag>
                      <el-tag type="info" effect="plain">{{ customerData.teachers.size }} {{ getPluralText('teacher', customerData.teachers.size) }}</el-tag>
                    </div>
                  </div>
                </template>
                
                <el-table :data="customerData.hoursArray" stripe border class="nested-table customer-nested-table">
                  <el-table-column prop="hour" :label="$t('booking.timeSlot')" width="120">
                    <template #default="{ row }">
                      <el-tag type="primary" effect="dark">{{ row.hour }}</el-tag>
                    </template>
                  </el-table-column>
                  
                  <el-table-column :label="$t('booking.hoursCount')" width="100" align="center">
                    <template #default="{ row }">
                      <el-tag type="primary" effect="plain" size="large">
                        {{ row.bookings.length }}
                      </el-tag>
                    </template>
                  </el-table-column>
                  
                  <el-table-column :label="$t('booking.teachers')" min-width="250">
                    <template #default="{ row }">
                      <div class="tag-list">
                        <el-tag 
                          v-for="(booking, idx) in row.bookings" 
                          :key="idx"
                          size="default"
                          type="success"
                          effect="light"
                          style="margin: 3px; cursor: pointer;"
                          @click="viewDetail(booking)"
                        >
                          <i class="iconfont icon-teacher" style="margin-right: 4px; font-size: 12px;"></i>
                          {{ booking.teacherName }}
                        </el-tag>
                      </div>
                    </template>
                  </el-table-column>
                  
                  <el-table-column :label="$t('common.actions')" width="120" align="center">
                    <template #default="{ row }">
                      <el-button 
                        v-for="(booking, idx) in row.bookings" 
                        :key="idx"
                        type="primary" 
                        size="small"
                        @click="viewDetail(booking)"
                        :icon="Search"
                      >
                        {{ $t('common.detail') }}
                      </el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </el-collapse-item>
            </el-collapse>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <el-pagination
        v-model:current-page="queryParams.page"
        v-model:page-size="queryParams.pageSize"
        :total="total"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        class="pagination"
      />
        </el-tab-pane>

        <!-- Tab 2: Booking History -->
        <el-tab-pane :label="$t('booking.bookingHistory')" name="history">
          <!-- Search Form for History -->
          <el-card class="search-card" shadow="never">
            <el-form :model="historyQueryParams" inline class="search-form">
              <el-form-item :label="$t('booking.customer')">
                <el-select 
                  v-model="historyQueryParams.customerName" 
                  clearable 
                  filterable
                  class="customer-select"
                  :placeholder="$t('booking.selectCustomer')"
                >
                  <template #prefix>
                    <i class="iconfont icon-helmet" style="font-size: 14px; color: #909399;"></i>
                  </template>
                  <el-option
                    v-for="customer in customerStore.customers"
                    :key="customer.id"
                    :label="customer.name"
                    :value="customer.name"
                  />
                </el-select>
              </el-form-item>
              
              <el-form-item :label="$t('booking.teacher')">
                <el-select 
                  v-model="historyQueryParams.teacherName" 
                  clearable 
                  filterable
                  class="teacher-select"
                  :placeholder="$t('booking.selectTeacher')"
                >
                  <template #prefix>
                    <i class="iconfont icon-teacher" style="font-size: 14px; color: #909399;"></i>
                  </template>
                  <el-option
                    v-for="teacher in teacherStore.teachers"
                    :key="teacher.id"
                    :label="teacher.name"
                    :value="teacher.name"
                  />
                </el-select>
              </el-form-item>
              
              <el-form-item :label="$t('booking.timeRange')" class="date-range-item">
                <QuickDateRangePicker v-model="historyDateRange" @change="handleHistoryDateChange" />
              </el-form-item>
              
              <el-form-item class="action-buttons">
                <el-button type="primary" @click="handleHistorySearch">
                  <el-icon><Search /></el-icon>
                  {{ $t('common.search') }}
                </el-button>
                <el-button @click="handleHistoryReset">
                  <el-icon><RefreshRight /></el-icon>
                  {{ $t('common.reset') }}
                </el-button>
              </el-form-item>
            </el-form>
          </el-card>

          <!-- Simple Booking List Table -->
          <div class="booking-list" v-loading="historyLoading">
            <div v-if="historyBookings.length === 0" class="empty-data">
              <el-empty :description="$t('booking.noBookingFound')" />
            </div>
            
            <el-table v-else :data="historyBookings" stripe border class="history-table">
              <el-table-column prop="bookingDate" :label="$t('booking.date')" width="140">
                <template #default="{ row }">
                  <div class="date-cell">
                    {{ formatHistoryDate(row.bookingDate) }}
                  </div>
                </template>
              </el-table-column>
              
              <el-table-column prop="hour" :label="$t('booking.time')" width="120">
                <template #default="{ row }">
                  <el-tag type="info" size="small">
                    {{ row.hour.toString().padStart(2, '0') }}:00
                  </el-tag>
                </template>
              </el-table-column>
              
              <el-table-column prop="customerName" :label="$t('booking.customer')" min-width="160">
                <template #default="{ row }">
                  <div class="customer-cell clickable-cell" @click="showCustomerInfo(row.customerVipId, row.customerName)">
                    <i class="iconfont icon-helmet" style="color: #f5a623; margin-right: 6px;"></i>
                    <span class="customer-underline">{{ row.customerName }}</span>
                  </div>
                </template>
              </el-table-column>
              
              <el-table-column prop="name" :label="$t('booking.teacher')" min-width="160">
                <template #default="{ row }">
                  <div class="teacher-cell">
                    <i class="iconfont icon-teacher" style="color: #409eff; margin-right: 6px;"></i>
                    {{ row.name }}
                  </div>
                </template>
              </el-table-column>
              
              <el-table-column :label="$t('common.actions')" width="100" align="center">
                <template #default="{ row }">
                  <el-button 
                    type="primary" 
                    size="small" 
                    @click="viewDetail({ id: row.id })"
                    :icon="Search"
                  >
                    {{ $t('common.detail') }}
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- History Pagination -->
          <el-pagination
        v-model:current-page="historyQueryParams.page"
        v-model:page-size="historyQueryParams.pageSize"
        :total="historyTotal"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next"
        @size-change="handleHistorySizeChange"
        @current-change="handleHistoryCurrentChange"
        class="pagination"
      />
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <!-- Customer Info Dialog -->
    <el-dialog
      v-model="showCustomerDialog"
      :title="$t('booking.customerInfo')"
      width="500px"
      destroy-on-close
    >
      <div v-loading="customerDialogLoading" class="customer-info-content">
        <div v-if="customerInfo" class="customer-info-wrapper">
          <div class="customer-avatar-section">
            <ImagePreview 
              v-if="customerInfo.avatar"
              :src="customerInfo.avatar"
              width="100px"
              height="100px"
              :round="true"
              :preview-title="customerInfo.name"
            />
            <div v-else class="avatar-placeholder">
              <i class="iconfont icon-helmet" style="font-size: 50px; color: #E6A23C;"></i>
            </div>
          </div>
          
          <el-descriptions :column="1" border class="customer-descriptions">
            <el-descriptions-item :label="$t('customer.name')">
              {{ customerInfo.name }}
            </el-descriptions-item>
            <el-descriptions-item :label="$t('customer.phone')">
              {{ customerInfo.phone || '-' }}
            </el-descriptions-item>
            <el-descriptions-item :label="$t('customer.gender')">
              {{ customerInfo.gender ? $t('customer.male') : $t('customer.female') }}
            </el-descriptions-item>
            <el-descriptions-item :label="$t('customer.age')">
              {{ customerInfo.age || '-' }}
            </el-descriptions-item>
            <el-descriptions-item :label="$t('customer.status')">
              <el-tag :type="customerInfo.status === 1 ? 'success' : 'info'" size="small">
                {{ customerInfo.status === 1 ? $t('customer.active') : $t('customer.inactive') }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item :label="$t('customer.remark')">
              {{ customerInfo.remark || '-' }}
            </el-descriptions-item>
            <el-descriptions-item :label="$t('customer.totalRemainingVipHours')">
              <span class="vip-hours">{{ customerInfo.totalRemainingVipHours }}</span>
              <el-tag v-if="customerInfo.hasActiveVipPackage" type="warning" size="small" effect="dark" style="margin-left: 8px;">
                {{ customerInfo.activeVipPackageCount }} {{ $t('customer.activeVipPackage') }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item :label="$t('common.createdAt')">
              {{ customerInfo.createdAt ? formatDate(customerInfo.createdAt) : '-' }}
            </el-descriptions-item>
          </el-descriptions>
        </div>
        <el-empty v-else :description="$t('common.noData')" />
      </div>
      <template #footer>
        <el-button @click="showCustomerDialog = false">{{ $t('common.close') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox, ElSelect, ElOption } from 'element-plus'
import { useBookingStore } from '@/stores/booking'
import { useCustomerStore } from '@/stores/customer'
import { useTeacherStore } from '@/stores/teacher'
import { Download, Plus, ArrowDown, Document, Tickets, Minus, ArrowRight, Search, User, InfoFilled } from '@element-plus/icons-vue'
import { exportToExcel, exportToCSV } from '@/utils/export'
import QuickDateRangePicker from '@/components/QuickDateRangePicker.vue'
import ImagePreview from '@/components/ImagePreview.vue'
import MTime from '@/utils/time-handler'

const router = useRouter()
const { t } = useI18n()
const bookingStore = useBookingStore()
const customerStore = useCustomerStore()
const teacherStore = useTeacherStore()

const loading = ref(false)
const total = ref(0)
const dateRange = ref([])
const exporting = ref(false)

// Tab state
const activeTab = ref('weekly') // 'weekly' or 'history'

// View mode state
const groupByMode = ref('hour') // 'hour', 'teacher', 'customer'

// For collapsible panels in teacher/customer views
const activeTeacherPanels = ref([])
const activeCustomerPanels = ref([])

// History tab state
const historyLoading = ref(false)
const historyTotal = ref(0)
const historyDateRange = ref([])
const historyAllBookings = ref([]) // All fetched bookings
const historyFilteredBookings = ref([]) // Filtered bookings for display
const historyQueryParams = reactive({
  page: 1,
  pageSize: 20,
  customerName: '',
  teacherName: '',
  startDate: '',
  endDate: ''
})

// Customer info dialog state
const showCustomerDialog = ref(false)
const customerDialogLoading = ref(false)
const customerInfo = ref(null)

// Computed property to flatten bookings into individual hour rows
const historyHourRows = computed(() => {
  const rows = []
  
  historyFilteredBookings.value.forEach(booking => {
    if (booking.bookingTimes && booking.bookingTimes.length > 0) {
      booking.bookingTimes.forEach(time => {
        if (time.bookedHours && time.bookedHours.length > 0) {
          time.bookedHours.forEach(hour => {
            rows.push({
              id: booking.id,
              bookingDate: time.bookingDate,
              bookingDayNumber: time.bookingDayNumber,
              hour: hour.hour,
              hourId: hour.id,
              name: booking.name,
              customerName: booking.customerName,
              customerVipId: booking.customerVipId,
              orderNumber: booking.orderNumber,
              createdAt: booking.createdAt
            })
          })
        }
      })
    }
  })
  
  // Sort by date and hour
  rows.sort((a, b) => {
    const dateCompare = new Date(a.bookingDate) - new Date(b.bookingDate)
    if (dateCompare !== 0) return dateCompare
    return a.hour - b.hour
  })
  
  return rows
})

// Computed property for paginated history bookings
const historyBookings = computed(() => {
  const start = (historyQueryParams.page - 1) * historyQueryParams.pageSize
  const end = start + historyQueryParams.pageSize
  return historyHourRows.value.slice(start, end)
})



// Week calendar data
const weekDays = ref([])

// Set default date range to today
const setDefaultDateRange = () => {
  const today = MTime.getCurrentDate()
  
  dateRange.value = [
    today,
    today
  ]
  
  // Use new server-side date format
  queryParams.startDate = MTime.formatQueryStartDate(today)
  queryParams.endDate = MTime.formatQueryEndDate(today)
  
  // Generate week calendar
  generateWeekDays()
}

const queryParams = reactive({
  page: 1,
  pageSize: 10,
  customerId: '',
  teacherName: '',
  status: '',
  startDate: '',
  endDate: ''
})

// Generate week days including today and next 6 days
const generateWeekDays = () => {
  const today = new Date()
  const days = []
  
  // Generate 7 days starting from today
  for (let i = 0; i < 7; i++) {
    const current = new Date(today)
    current.setDate(today.getDate() + i)
    days.push(current)
  }
  
  weekDays.value = days
}

// Get day name (e.g., "Monday", "Tuesday")
const getDayName = (date) => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  return days[date.getDay()]
}

// Get day date in format "DD 日"
const getDayDate = (date) => {
  const day = date.getDate()
  return `${day}日`
}

// Check if a day is selected (matches current single day selection)
const isDaySelected = (day) => {
  if (!dateRange.value || dateRange.value.length !== 2) return false
  
  const startDate = dateRange.value[0]
  const endDate = dateRange.value[1]
  
  if (startDate === endDate) {
    const selectedDate = new Date(startDate)
    return day.toDateString() === selectedDate.toDateString()
  }
  
  return false
}

// Check if a day is today
const isToday = (day) => {
  const today = new Date()
  return day.toDateString() === today.toDateString()
}

// Select a specific day and filter bookings by that day
const selectDay = (day) => {
  const dayString = day.getFullYear() + '-' + 
    String(day.getMonth() + 1).padStart(2, '0') + '-' + 
    String(day.getDate()).padStart(2, '0')
  
  dateRange.value = [dayString, dayString]
  
  // Use new server-side date format for queries
  queryParams.startDate = MTime.formatQueryStartDate(dayString)
  queryParams.endDate = MTime.formatQueryEndDate(dayString)
  
  queryParams.page = 1
  fetchData()
}

// Get booking times from booking data
const getWeekdayFromBooking = (booking) => {
  if (!booking.bookingTimes || booking.bookingTimes.length === 0) return ''
  
  const firstTime = booking.bookingTimes[0]
  if (!firstTime.bookingDate) return ''
  
  const date = new Date(firstTime.bookingDate)
  const weekDays = [
    t('schedule.weekDays.sunday'),
    t('schedule.weekDays.monday'),
    t('schedule.weekDays.tuesday'),
    t('schedule.weekDays.wednesday'),
    t('schedule.weekDays.thursday'),
    t('schedule.weekDays.friday'),
    t('schedule.weekDays.saturday')
  ]
  return weekDays[date.getDay()]
}

const formatBookingDate = (booking) => {
  if (!booking.bookingTimes || booking.bookingTimes.length === 0) return '-'
  
  const firstTime = booking.bookingTimes[0]
  if (!firstTime.bookingDate) return '-'
  
  const date = new Date(firstTime.bookingDate)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  
  const weekDays = [
    t('schedule.weekDays.sunday'),
    t('schedule.weekDays.monday'),
    t('schedule.weekDays.tuesday'),
    t('schedule.weekDays.wednesday'),
    t('schedule.weekDays.thursday'),
    t('schedule.weekDays.friday'),
    t('schedule.weekDays.saturday')
  ]
  const weekDay = weekDays[date.getDay()]
  
  return `${year}/${month}/${day}${weekDay}`
}

const formatHistoryDate = (dateString) => {
  if (!dateString) return '-'
  
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  
  const weekDays = [
    t('schedule.weekDays.sunday'),
    t('schedule.weekDays.monday'),
    t('schedule.weekDays.tuesday'),
    t('schedule.weekDays.wednesday'),
    t('schedule.weekDays.thursday'),
    t('schedule.weekDays.friday'),
    t('schedule.weekDays.saturday')
  ]
  const weekDay = weekDays[date.getDay()]
  
  return `${year}/${month}/${day} ${weekDay}`
}

const getBookingTimeRange = (booking) => {
  if (!booking.bookingTimes || booking.bookingTimes.length === 0) return '-'
  
  const firstTime = booking.bookingTimes[0]
  if (!firstTime.bookedHours || firstTime.bookedHours.length === 0) return '-'
  
  const hours = firstTime.bookedHours.map(h => h.hour).sort((a, b) => a - b)
  const startHour = hours[0].toString().padStart(2, '0')
  const endHour = (hours[hours.length - 1] + 1).toString().padStart(2, '0')
  
  return `${startHour}:00-${endHour}:00`
}

const getBookingHours = (booking) => {
  if (!booking.bookingTimes || booking.bookingTimes.length === 0) return []
  
  const hours = []
  booking.bookingTimes.forEach(time => {
    if (time.bookedHours && time.bookedHours.length > 0) {
      time.bookedHours.forEach(hour => {
        hours.push(`${hour.hour.toString().padStart(2, '0')}:00`)
      })
    }
  })
  
  return hours.sort()
}

const getBookingTimes = (booking) => {
  if (!booking.bookingTimes || booking.bookingTimes.length === 0) return []
  
  const times = []
  booking.bookingTimes.forEach(time => {
    if (time.bookedHours && time.bookedHours.length > 0) {
      time.bookedHours.forEach(hour => {
        times.push(`${hour.hour.toString().padStart(2, '0')}:00`)
      })
    }
  })
  return times
}

// Helper function to calculate total hours in a booking
const calculateBookingHours = (booking) => {
  if (!booking.bookingTimes || booking.bookingTimes.length === 0) return 0
  let totalHours = 0
  booking.bookingTimes.forEach(time => {
    if (time.bookedHours && time.bookedHours.length > 0) {
      totalHours += time.bookedHours.length
    }
  })
  return totalHours
}

// Helper function to get earliest hour from booking
const getEarliestHour = (booking) => {
  if (!booking.bookingTimes || booking.bookingTimes.length === 0) return 999
  let earliest = 999
  booking.bookingTimes.forEach(time => {
    if (time.bookedHours && time.bookedHours.length > 0) {
      const minHour = Math.min(...time.bookedHours.map(h => h.hour))
      earliest = Math.min(earliest, minHour)
    }
  })
  return earliest
}

// Helper function to get booking date
const getBookingDate = (booking) => {
  if (!booking.bookingTimes || booking.bookingTimes.length === 0) return ''
  return booking.bookingTimes[0].bookingDate || ''
}

// Get customer breakdown for a teacher's bookings
const getCustomerBreakdown = (bookings) => {
  const breakdown = {}
  bookings.forEach(booking => {
    const customer = booking.customerName || 'Unknown'
    const hours = calculateBookingHours(booking)
    breakdown[customer] = (breakdown[customer] || 0) + hours
  })
  return breakdown
}

// Get teacher breakdown for a customer's bookings
const getTeacherBreakdown = (bookings) => {
  const breakdown = {}
  bookings.forEach(booking => {
    const teacher = booking.name || 'Unknown'
    const hours = calculateBookingHours(booking)
    breakdown[teacher] = (breakdown[teacher] || 0) + hours
  })
  return breakdown
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const day = date.getDate()
  return `${year}/${month}/${day}`
}

const getStatusType = (status) => {
  const typeMap = {
    pending: 'warning',
    confirmed: 'success',
    completed: 'info',
    cancelled: 'danger'
  }
  return typeMap[status] || 'info'
}

const getStatusText = (status) => {
  const textMap = {
    pending: t('booking.pending'),
    confirmed: t('booking.confirmed'),
    completed: t('booking.completed'),
    cancelled: t('booking.cancelled')
  }
  return textMap[status] || status
}

// Helper function for pluralization
const getPluralText = (key, count) => {
  if (count === 1) {
    return t(`booking.${key}Singular`)
  }
  return t(`booking.${key}`)
}

// Computed properties for grouped data
// Group bookings by teacher
const bookingsByTeacher = computed(() => {
  const grouped = {}
  bookingStore.bookings.forEach(booking => {
    const teacherName = booking.name || 'Unknown Teacher'
    if (!grouped[teacherName]) {
      grouped[teacherName] = {
        teacherName,
        bookings: [],
        totalHours: 0
      }
    }
    grouped[teacherName].bookings.push(booking)
    grouped[teacherName].totalHours += calculateBookingHours(booking)
  })
  
  // Sort based on sortOrder
  return Object.values(grouped).sort((a, b) => {
    if (sortOrder.value === 'name') return a.teacherName.localeCompare(b.teacherName)
    if (sortOrder.value === 'hour') return b.totalHours - a.totalHours
    return 0
  })
})

// Group bookings by customer
const bookingsByCustomer = computed(() => {
  const grouped = {}
  bookingStore.bookings.forEach(booking => {
    const customerName = booking.customerName || 'Unknown Customer'
    if (!grouped[customerName]) {
      grouped[customerName] = {
        customerName,
        bookings: [],
        totalHours: 0
      }
    }
    grouped[customerName].bookings.push(booking)
    grouped[customerName].totalHours += calculateBookingHours(booking)
  })
  
  return Object.values(grouped).sort((a, b) => {
    if (sortOrder.value === 'name') return a.customerName.localeCompare(b.customerName)
    if (sortOrder.value === 'hour') return b.totalHours - a.totalHours
    return 0
  })
})

// Sort bookings by hour (for default view)
const sortedBookings = computed(() => {
  const bookings = [...bookingStore.bookings]
  
  if (sortOrder.value === 'hour') {
    return bookings.sort((a, b) => {
      const aHours = getEarliestHour(a)
      const bHours = getEarliestHour(b)
      return aHours - bHours
    })
  } else if (sortOrder.value === 'date') {
    return bookings.sort((a, b) => {
      const aDate = getBookingDate(a)
      const bDate = getBookingDate(b)
      return new Date(aDate) - new Date(bDate)
    })
  }
  
  return bookings
})

// Helper function to extract customer ID from booking object
const getCustomerIdFromBooking = (booking) => {
  if (!booking) return null
  return booking.customerId || booking.customer?.id || null
}

// Extract all unique hours from bookings and group them
const bookingsByHour = computed(() => {
  const hourMap = {}
  
  bookingStore.bookings.forEach(booking => {
    if (!booking.bookingTimes || booking.bookingTimes.length === 0) return
    
    booking.bookingTimes.forEach(time => {
      if (!time.bookedHours || time.bookedHours.length === 0) return
      
      time.bookedHours.forEach(hourObj => {
        const hourKey = `${hourObj.hour.toString().padStart(2, '0')}:00`
        
        if (!hourMap[hourKey]) {
          hourMap[hourKey] = {
            hour: hourKey,
            hourNumber: hourObj.hour,
            bookings: [],
            teachers: new Set(),
            customers: new Set(),
            customerMap: new Map() // Map customerName -> customerId
          }
        }
        
        hourMap[hourKey].bookings.push({
          ...booking,
          bookingDate: time.bookingDate,
          bookedHour: hourObj.hour
        })
        hourMap[hourKey].teachers.add(booking.name || 'Unknown')
        hourMap[hourKey].customers.add(booking.customerName || 'Unknown')
        // Store customerId mapping for quick lookup
        const customerId = getCustomerIdFromBooking(booking)
        if (customerId && booking.customerName) {
          hourMap[hourKey].customerMap.set(booking.customerName, customerId)
        }
      })
    })
  })
  
  // Convert to array and sort by hour (chronological order)
  const result = Object.values(hourMap).sort((a, b) => a.hourNumber - b.hourNumber)
  
  return result
})

// Group bookings by teacher with hour breakdown
const bookingsByTeacherWithHours = computed(() => {
  const teacherMap = {}
  
  bookingStore.bookings.forEach(booking => {
    const teacherName = booking.name || 'Unknown Teacher'
    
    if (!teacherMap[teacherName]) {
      teacherMap[teacherName] = {
        teacher: teacherName,
        hours: {},
        totalBookings: 0,
        customers: new Set()
      }
    }
    
    if (!booking.bookingTimes || booking.bookingTimes.length === 0) return
    
    booking.bookingTimes.forEach(time => {
      if (!time.bookedHours || time.bookedHours.length === 0) return
      
      time.bookedHours.forEach(hourObj => {
        const hourKey = `${hourObj.hour.toString().padStart(2, '0')}:00`
        
        if (!teacherMap[teacherName].hours[hourKey]) {
          teacherMap[teacherName].hours[hourKey] = []
        }
        
        teacherMap[teacherName].hours[hourKey].push({
          ...booking,
          bookingDate: time.bookingDate,
          customerName: booking.customerName
        })
        teacherMap[teacherName].totalBookings++
        teacherMap[teacherName].customers.add(booking.customerName || 'Unknown')
      })
    })
  })
  
  // Convert to array
  let result = Object.values(teacherMap).map(teacher => ({
    ...teacher,
    hoursArray: Object.entries(teacher.hours)
      .map(([hour, bookings]) => ({ hour, bookings }))
      .sort((a, b) => {
        const hourA = parseInt(a.hour.split(':')[0])
        const hourB = parseInt(b.hour.split(':')[0])
        return hourA - hourB
      })
  }))
  
  // Sort by teacher name (alphabetical)
  result.sort((a, b) => a.teacher.localeCompare(b.teacher))
  
  return result
})

// Group bookings by customer with hour breakdown
const bookingsByCustomerWithHours = computed(() => {
  const customerMap = {}
  
  bookingStore.bookings.forEach(booking => {
    const customerName = booking.customerName || 'Unknown Customer'
    
    if (!customerMap[customerName]) {
      customerMap[customerName] = {
        customer: customerName,
        hours: {},
        totalBookings: 0,
        teachers: new Set()
      }
    }
    
    if (!booking.bookingTimes || booking.bookingTimes.length === 0) return
    
    booking.bookingTimes.forEach(time => {
      if (!time.bookedHours || time.bookedHours.length === 0) return
      
      time.bookedHours.forEach(hourObj => {
        const hourKey = `${hourObj.hour.toString().padStart(2, '0')}:00`
        
        if (!customerMap[customerName].hours[hourKey]) {
          customerMap[customerName].hours[hourKey] = []
        }
        
        customerMap[customerName].hours[hourKey].push({
          ...booking,
          bookingDate: time.bookingDate,
          teacherName: booking.name
        })
        customerMap[customerName].totalBookings++
        customerMap[customerName].teachers.add(booking.name || 'Unknown')
      })
    })
  })
  
  // Convert to array
  let result = Object.values(customerMap).map(customer => ({
    ...customer,
    hoursArray: Object.entries(customer.hours)
      .map(([hour, bookings]) => ({ hour, bookings }))
      .sort((a, b) => {
        const hourA = parseInt(a.hour.split(':')[0])
        const hourB = parseInt(b.hour.split(':')[0])
        return hourA - hourB
      })
  }))
  
  // Sort by customer name (alphabetical)
  result.sort((a, b) => a.customer.localeCompare(b.customer))
  
  return result
})

const fetchData = async () => {
  loading.value = true
  const res = await bookingStore.fetchBookings(queryParams)
  if (res.success) {
    total.value = bookingStore.total
  }
  loading.value = false
}

const handleQuickDateChange = ({ startDate, endDate }) => {
  queryParams.startDate = startDate
  queryParams.endDate = endDate
  generateWeekDays()
  fetchData()
}

const handleSearch = () => {
  if (dateRange.value && dateRange.value.length === 2) {
    // Use new server-side date format for queries
    queryParams.startDate = MTime.formatQueryStartDate(dateRange.value[0])
    
    queryParams.endDate = MTime.formatQueryEndDate(dateRange.value[1])
    
    // Generate week calendar
    generateWeekDays()
  } else {
    setDefaultDateRange()
  }
  
  queryParams.page = 1
  fetchData()
}

const handleReset = () => {
  queryParams.customerId = ''
  queryParams.teacherName = ''
  queryParams.status = ''
  dateRange.value = []
  setDefaultDateRange()
  fetchData()
}

const handleSizeChange = (val) => {
  queryParams.pageSize = val
  fetchData()
}

const handleCurrentChange = (val) => {
  queryParams.page = val
  fetchData()
}

// History tab functions
const fetchHistoryData = async () => {
  historyLoading.value = true
  
  // Fetch ALL bookings without pagination - we'll filter locally
  const fetchParams = {
    startDate: historyQueryParams.startDate,
    endDate: historyQueryParams.endDate,
    page: 1,
    pageSize: 10000 // Fetch all data to filter locally
  }
  
  const res = await bookingStore.fetchBookings(fetchParams)
  if (res.success) {
    historyAllBookings.value = bookingStore.bookings
    historyTotal.value = bookingStore.total
    applyHistoryFilters()
  }
  historyLoading.value = false
}

// Apply customer and teacher filters on local data
const applyHistoryFilters = () => {
  let filtered = [...historyAllBookings.value]
  
  // Filter by customer
  if (historyQueryParams.customerName) {
    filtered = filtered.filter(booking => {
      return booking.customerName === historyQueryParams.customerName
    })
  }
  
  // Filter by teacher
  if (historyQueryParams.teacherName) {
    filtered = filtered.filter(booking => {
      return booking.name === historyQueryParams.teacherName
    })
  }
  
  historyFilteredBookings.value = filtered
  
  // Count total hours, not bookings
  let totalHours = 0
  filtered.forEach(booking => {
    if (booking.bookingTimes && booking.bookingTimes.length > 0) {
      booking.bookingTimes.forEach(time => {
        if (time.bookedHours && time.bookedHours.length > 0) {
          totalHours += time.bookedHours.length
        }
      })
    }
  })
  
  historyTotal.value = totalHours
  historyQueryParams.page = 1
}

const handleHistoryDateChange = ({ startDate, endDate }) => {
  historyQueryParams.startDate = startDate
  historyQueryParams.endDate = endDate
  historyQueryParams.page = 1
  fetchHistoryData()
}

const handleHistorySearch = () => {
  if (historyDateRange.value && historyDateRange.value.length === 2) {
    historyQueryParams.startDate = MTime.formatQueryStartDate(historyDateRange.value[0])
    historyQueryParams.endDate = MTime.formatQueryEndDate(historyDateRange.value[1])
  }
  
  historyQueryParams.page = 1
  fetchHistoryData()
}

const handleHistoryReset = () => {
  historyQueryParams.customerName = ''
  historyQueryParams.teacherName = ''
  historyDateRange.value = []
  historyQueryParams.startDate = ''
  historyQueryParams.endDate = ''
  historyQueryParams.page = 1
  historyAllBookings.value = []
  historyFilteredBookings.value = []
  historyTotal.value = 0
  fetchHistoryData()
}

const handleHistorySizeChange = (val) => {
  historyQueryParams.pageSize = val
  // No need to refetch, just recompute pagination
}

const handleHistoryCurrentChange = (val) => {
  historyQueryParams.page = val
  // No need to refetch, just recompute pagination
}

// Watch for customer/teacher filter changes - filter locally without API call
watch(() => historyQueryParams.customerName, () => {
  applyHistoryFilters()
})

watch(() => historyQueryParams.teacherName, () => {
  applyHistoryFilters()
})

const goToAdd = () => {
  router.push('/booking/add')
}

const getCustomerIdByName = (bookings, customerName) => {
  const booking = bookings.find(b => b.customerName === customerName)
  return getCustomerIdFromBooking(booking)
}

const getCustomerIdFromCustomerData = (customerData) => {
  if (!customerData.hoursArray || customerData.hoursArray.length === 0) return null
  const firstHour = customerData.hoursArray[0]
  if (!firstHour.bookings || firstHour.bookings.length === 0) return null
  return getCustomerIdFromBooking(firstHour.bookings[0])
}

const showCustomerInfo = async (customerId, customerName = null) => {
  let id = customerId
  
  showCustomerDialog.value = true
  customerDialogLoading.value = true
  customerInfo.value = null
  
  try {
    // First try to find customer by name (most reliable since booking API may return VIP ID)
    if (customerName) {
      // If customers aren't loaded yet, fetch them
      if (customerStore.customers.length === 0) {
        await customerStore.fetchCustomers()
      }
      
      const customer = customerStore.customers.find(c => c.name === customerName)
      if (customer) {
        id = customer.id
      }
    }
    
    // If we have a valid ID, fetch customer details
    if (id && id !== null) {
      const res = await customerStore.fetchCustomerById(id)
      if (res.success) {
        customerInfo.value = res.data
      } else {
        ElMessage.warning(t('customer.notFound'))
      }
    } else {
      console.warn('No customer ID provided and could not find customer by name')
      ElMessage.warning(t('customer.notFound'))
    }
  } catch (error) {
    console.error('Failed to fetch customer info:', error)
    ElMessage.error(t('common.loadFailed'))
  } finally {
    customerDialogLoading.value = false
  }
}

const viewDetail = (row) => {
  // Store the booking data in Pinia store before navigation
  bookingStore.currentBooking = row
  
  // Navigate with just the ID - no need to pass large objects
  router.push(`/booking/detail/${row.id}`)
}

// Expand/Collapse All functions for teacher and customer views
const expandAllTeachers = () => {
  activeTeacherPanels.value = bookingsByTeacherWithHours.value.map((_, index) => index)
}

const collapseAllTeachers = () => {
  activeTeacherPanels.value = []
}

const expandAllCustomers = () => {
  activeCustomerPanels.value = bookingsByCustomerWithHours.value.map((_, index) => index)
}

const collapseAllCustomers = () => {
  activeCustomerPanels.value = []
}

const handleConfirm = async (row) => {
  try {
    await ElMessageBox.confirm(
      t('booking.confirmBookingMessage', { name: row.customerName }),
      t('booking.confirmBooking'),
      { type: 'success' }
    )
    await bookingStore.confirmBookingAction(row.id)
    ElMessage.success(t('common.success'))
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      console.error(error)
    }
  }
}

const handleExportCommand = (format) => {
  if (format === 'csv') {
    handleExport('csv')
  } else {
    handleExport('xlsx')
  }
}

const handleExport = async (format = 'xlsx') => {
  try {
    exporting.value = true
    
    let bookingsToExport = []
    
    if (activeTab.value === 'weekly') {
      // Export weekly schedule data - fetch all filtered data
      ElMessage.info(t('common.loading'))
      
      // Save current page params
      const originalPage = queryParams.page
      const originalPageSize = queryParams.pageSize
      
      // Fetch all data by setting a large page size
      queryParams.page = 1
      queryParams.pageSize = 10000 // Large enough to get all data
      
      const res = await bookingStore.fetchBookings(queryParams)
      if (res.success) {
        bookingsToExport = bookingStore.bookings
      }
      
      // Restore original pagination params
      queryParams.page = originalPage
      queryParams.pageSize = originalPageSize
      
      // Re-fetch to restore the display data
      await bookingStore.fetchBookings(queryParams)
      
    } else if (activeTab.value === 'history') {
      // Export booking history data - use filtered data (not paginated)
      bookingsToExport = historyFilteredBookings.value
    }
    
    if (bookingsToExport.length === 0) {
      ElMessage.warning(t('common.noData'))
      return
    }
    
    // Transform data for export - correctly handling the actual API response structure
    const exportData = bookingsToExport.map(booking => {
      const bookingTimes = booking.bookingTimes || []
      const times = []
      let bookingDate = ''
      
      // Extract all booked hours and dates
      bookingTimes.forEach(time => {
        if (time.bookedHours?.length > 0) {
          time.bookedHours.forEach(hour => {
            times.push(`${hour.hour.toString().padStart(2, '0')}:00`)
          })
        }
        
        // Get the booking date from the first booking time
        if (!bookingDate && time.bookingDate) {
          bookingDate = new Date(time.bookingDate).toLocaleDateString()
        }
      })
      
      return {
        'ID': booking.id,
        'Order Number': booking.orderNumber || '',
        'Customer': booking.customerName || '',
        'Teacher': booking.name || '',
        'Date': bookingDate,
        'Times': times.join(', '),
        'Created At': booking.createdAt ? new Date(booking.createdAt).toLocaleString() : ''
      }
    })
    
    // Export using utility based on format
    if (format === 'csv') {
      exportToCSV(exportData, 'bookings')
    } else {
      exportToExcel(exportData, 'bookings', 'Bookings')
    }
    
    ElMessage.success(t('common.exportSuccess') || 'Export successful')
    
  } catch (error) {
    console.error('Export failed:', error)
    ElMessage.error(error.message || t('common.exportFailed'))
  } finally {
    exporting.value = false
  }
}



// Watch for tab changes and refetch data when switching back to weekly tab
watch(activeTab, (newTab, oldTab) => {
  if (newTab === 'weekly' && oldTab === 'history') {
    // When switching from history to weekly, refetch weekly bookings
    fetchData()
  }
})

onMounted(() => {
  setDefaultDateRange()
  customerStore.fetchCustomers() // Load customers for filter dropdown
  teacherStore.fetchTeachers() // Load teachers for filter dropdown
  fetchData()
})
</script>

<style scoped lang="scss">
.booking-management-card {
  margin-bottom: 20px;
  
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .header-left {
      display: flex;
      align-items: center;
      gap: 20px;
      
      .page-title {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        color: #1f2329;
      }
    }
    
    .header-right {
      display: flex;
      gap: 12px;
    }
    
    .export-btn {
      background: linear-gradient(90deg, #67c23a 0%, #5daf34 100%);
      border: none;
      padding: 10px 24px;
      font-weight: 500;
      
      &:hover {
        opacity: 0.9;
      }
    }
    
    .add-btn {
      background: linear-gradient(90deg, #f5a623 0%, #f0931e 100%);
      border: none;
      padding: 10px 24px;
      font-weight: 500;
      
      &:hover {
        opacity: 0.9;
      }
    }
  }
}

.search-card {
  margin-bottom: 16px;
  
  .search-form {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px;
    justify-content: flex-start;
    
    .el-form-item {
      margin-bottom: 0 !important;
    }
    
    .customer-select,
    .teacher-select {
      width: 180px;
    }
    
    .date-range-item {
      width: 100%;
      order: -1;
      
      :deep(.el-form-item__label) {
        min-width: 80px;
      }
      
      :deep(.el-form-item__content) {
        justify-content: flex-start;
      }
    }
    
    .action-buttons {
      display: flex;
      gap: 8px;
      flex-shrink: 0;
      
      .el-button {
        min-width: 80px;
      }
    }
  }
}

.week-calendar-card {
  margin-bottom: 20px;
  
  .week-calendar {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding: 12px 0;
    
    .day-item {
      min-width: 80px;
      height: 80px;
      border-radius: 12px;
      background: var(--bg-color-white, #ffffff);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
      border: 2px solid var(--border-color-light, #e4e7ed);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      
      .day-name,
      .day-date {
        color: var(--color-text-primary, #303133);
      }
      
      &:hover {
        background: var(--bg-color-page-light, #f5f7fa);
        transform: translateY(-3px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        border-color: var(--border-color, #c0c4cc);
      }
      
      &.is-selected {
        background: var(--color-warning, #f5a623);
        color: #fff;
        box-shadow: 0 4px 12px rgba(245, 166, 35, 0.4);
        border-color: var(--color-warning, #f5a623);
        
        .day-name,
        .day-date {
          color: #fff;
        }
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(245, 166, 35, 0.5);
        }
      }
      
      .day-name {
        font-size: 13px;
        margin-bottom: 6px;
        font-weight: 500;
        opacity: 0.8;
      }
      
      .day-date {
        font-size: 20px;
        font-weight: 700;
      }
    }
  }
  
  .view-controls-inline {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    
    .control-label {
      font-weight: 600;
      color: var(--color-text-primary, #303133);
      font-size: 14px;
    }
  }
}

.booking-list {
  .empty-data {
    padding: 40px 0;
  }
  
  .booking-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
  }
  
  .booking-card {
    background: var(--bg-color-white, #fff);
    border-radius: 8px;
    border: 1px solid var(--border-color-light, #e4e7ed);
    padding: 16px;
    transition: all 0.3s;
    
    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
      border-color: var(--color-primary, #667eea);
    }
    
    .card-header-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      padding-bottom: 12px;
      border-bottom: 1px solid var(--border-color-lighter, #f0f0f0);
      
      .coach-info {
        display: flex;
        align-items: center;
        gap: 12px;
        
        .coach-title {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--color-text-primary, #1f2329);
          display: flex;
          align-items: center;
          gap: 8px;
          
          .coach-number {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 24px;
            height: 24px;
            padding: 0 6px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: #fff;
            border-radius: 12px;
            font-size: 13px;
            font-weight: 700;
            flex-shrink: 0;
          }
        }
        
        .weekday-badge {
          background: var(--color-warning, #f5a623);
          color: var(--color-white, #fff);
          border-color: var(--color-warning, #f5a623);
          font-weight: 600;
        }
              
        .status-tag {
          flex-shrink: 0;
        }
      }
    }
    
    .card-content {
      margin-bottom: 16px;
      
      .info-row {
        display: flex;
        align-items: flex-start;
        margin-bottom: 8px;
        gap: 8px;
        
        &:last-child {
          margin-bottom: 0;
        }
        
        .label {
          font-size: 14px;
          color: var(--color-text-regular, #606266);
          font-weight: 500;
          flex-shrink: 0;
          min-width: 80px;
        }
        
        .value {
          font-size: 14px;
          color: var(--color-text-primary, #1f2329);
          flex: 1;
        }
        
        .time-tag {
          margin-right: 4px;
          background: var(--color-primary-light-9, #ecf5ff);
          color: var(--color-primary, #409eff);
          border-color: var(--color-primary-light-7, #d9ecff);
        }
        
        .date-value {
          color: var(--color-primary, #409eff);
          font-weight: 600;
        }
      }
    }
    
    .card-actions {
      display: flex;
      gap: 8px;
      padding-top: 12px;
      border-top: 1px solid var(--border-color-lighter, #f0f0f0);
      
      .el-button {
        flex: 1;
      }
    }
  }
}

.pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--border-color-lighter, #f0f0f0);
}

.booking-table-container {
  .booking-table {
    margin-top: 16px;
    
    :deep(.el-table__header) {
      th {
        background: var(--bg-color-page-light, #f5f7fa);
        font-weight: 600;
      }
    }
  }
  
  .hour-view-table {
    :deep(.el-table__body) {
      tr {
        transition: all 0.3s ease;
        
        &:hover {
          background: var(--bg-color-page-light, #f5f7fa) !important;
        }
      }
      
      td {
        padding: 10px 0;
        vertical-align: middle;
      }
    }
    
    .booking-link {
      font-size: 14px;
      font-weight: 500;
      padding: 4px 8px;
      border-radius: 4px;
      transition: all 0.3s ease;
      
      &:hover {
        background: var(--color-primary-light-9, #ecf5ff);
        transform: translateX(4px);
      }
    }
  }
  
  .history-table {
    margin-top: 16px;
    
    :deep(.el-table__header) {
      th {
        background: var(--bg-color-page-light, #f5f7fa);
        font-weight: 600;
        color: var(--color-text-primary, #303133);
      }
    }
    
    :deep(.el-table__body) {
      tr {
        transition: all 0.3s ease;
        
        &:hover {
          background: var(--bg-color-page-light, #f5f7fa) !important;
        }
      }
      
      td {
        padding: 12px 0;
      }
    }
    
    .date-cell {
      font-weight: 500;
      color: var(--color-text-primary, #303133);
    }
    
    .customer-cell,
    .teacher-cell {
      display: flex;
      align-items: center;
      font-weight: 500;
    }
  }
  
  .tag-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  
  .booking-details-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    
    .booking-detail-item {
      padding: 6px 0;
      border-bottom: 1px dashed var(--border-color-lighter, #e4e7ed);
      
      &:last-child {
        border-bottom: none;
      }
    }
  }
}

.grouped-view {
  margin-top: 16px;
  
  .collapse-controls {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
  }
  
  .teacher-collapse,
  .customer-collapse {
    :deep(.el-collapse-item__header) {
      height: auto;
      padding: 16px 20px;
      background: var(--bg-color-page-light, #f5f7fa);
      border-radius: 8px;
      margin-bottom: 8px;
      transition: all 0.3s ease;
      
      &:hover {
        background: var(--border-color-lighter, #e4e7ed);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      }
      
      &.is-active {
        background: var(--color-primary-light-9, #ecf5ff);
        border-left: 4px solid var(--color-primary, #409eff);
      }
    }
    
    :deep(.el-collapse-item__arrow) {
      display: none; // Hide default arrow
    }
    
    .collapse-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      width: 100%;
      
      .header-main {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
        min-width: 0;
        
        .expand-icon {
          font-size: 18px;
          color: var(--color-primary, #409eff);
          transition: transform 0.3s ease;
          flex-shrink: 0;
        }
        
        h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-color-primary, #303133);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .customer-info-icon {
          color: #E6A23C;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.3s ease;
          
          &:hover {
            background-color: rgba(230, 162, 60, 0.1);
            transform: scale(1.1);
          }
        }
      }
      
      .header-stats {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
        
        .el-tag {
          font-weight: 500;
        }
      }
    }
    
    :deep(.el-collapse-item__wrap) {
      border: none;
      background: transparent;
    }
    
    :deep(.el-collapse-item__content) {
      padding: 12px 0;
    }
    
    :deep(.el-collapse-item.is-active) {
      .expand-icon {
        transform: rotate(90deg);
      }
    }
  }
  
  .nested-table {
    margin-top: 8px;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    
    :deep(.el-table__body) {
      tr {
        transition: all 0.3s ease;
        
        &:hover {
          background: var(--bg-color-page-light, #f5f7fa) !important;
        }
      }
      
      td {
        padding: 10px 0;
        vertical-align: middle;
      }
    }
  }
  
  .teacher-nested-table,
  .customer-nested-table {
    :deep(.el-table__body) {
      tr:hover {
        background: var(--bg-color-page-light, #f5f7fa) !important;
      }
    }
  }
}

.teacher-group,
.customer-group {
  margin-bottom: 24px;
  background: var(--bg-color-white, #fff);
  border-radius: 8px;
  padding: 16px;
  border: 1px solid var(--border-color-light, #e4e7ed);
  
  .group-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 2px solid var(--border-color-lighter, #f0f0f0);
    
    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: var(--color-text-primary, #1f2329);
    }
  }
  
  .summary-section {
    margin-bottom: 16px;
    padding: 12px;
    background: var(--bg-color-page-light, #f5f7fa);
    border-radius: 6px;
    
    h4 {
      margin: 0 0 8px 0;
      font-size: 14px;
      color: var(--color-text-regular, #606266);
    }
    
    .customer-list,
    .teacher-list {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }
  }
  
  .group-bookings {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 12px;
  }
}

.booking-card.compact {
  padding: 12px;
  
  .card-content {
    .info-row {
      margin-bottom: 6px;
      
      .label {
        min-width: 70px;
        font-size: 13px;
      }
      
      .value {
        font-size: 13px;
      }
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .booking-cards {
    grid-template-columns: 1fr;
  }
  
  .search-form {
    .customer-select,
    .teacher-select {
      width: 100%;
    }
    
    .date-range-item {
      width: 100%;
      
      :deep(.el-date-editor) {
        width: 100%;
      }
    }
  }
  
  .week-calendar {
    gap: 8px;
    
    .day-item {
      min-width: 70px;
      height: 70px;
      
      .day-name {
        font-size: 12px;
      }
      
      .day-date {
        font-size: 18px;
      }
    }
  }
}

.customer-info-content {
  min-height: 200px;
  
  .customer-info-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    
    .customer-avatar-section {
      margin-bottom: 20px;
      
      :deep(.image-preview-container.is-round) {
        border: 3px solid #f5a623;
        box-shadow: 0 4px 12px rgba(245, 166, 35, 0.2);
      }
      
      .avatar-placeholder {
        width: 100px;
        height: 100px;
        border-radius: 50%;
        background-color: #f5f7fa;
        border: 3px solid #f5a623;
        box-shadow: 0 4px 12px rgba(245, 166, 35, 0.2);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #909399;
        
        .el-icon {
          color: #f5a623;
        }
      }
    }
    
    .customer-descriptions {
      width: 100%;
      
      .vip-hours {
        font-weight: bold;
        color: #f5a623;
        font-size: 16px;
      }
    }
  }
}

.customer-name-link {
  color: #f5a623;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
}

.customer-underline {
  text-decoration: underline;
  text-underline-offset: 2px;
  text-decoration-thickness: 1px;
  color: #f5a623;
  transition: all 0.3s ease;
  
  &:hover {
    text-decoration-thickness: 2px;
  }
}

.clickable-customer-tag {
  margin: 3px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(245, 166, 35, 0.3);
  }
}

.clickable-cell {
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 4px 8px;
  border-radius: 4px;
  
  &:hover {
    background-color: rgba(245, 166, 35, 0.1);
    transform: translateX(2px);
  }
}

.booking-link-row {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>